import React, { useEffect, useRef, useState } from "react";
import { Box, ButtonBase } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-hot-toast";
import useCallConnection from "../hooks/useCallConnection";
import useLiveCallsFeed from "../hooks/useLiveCallsFeed";
import { clearCallError } from "../../../redux/Slice/Calls/callSlice";
import { showBrowserNotification } from "../../../utils/notificationUtils";
import CallWidget from "./CallWidget";
import TransferRequestWidget from "./TransferRequestWidget";

// Served straight from the frontend's own public/ folder — same convention
// as the existing message-notification sound (notificationUtils.js). No
// reason for a ringtone to go through the API server at all, and doing so
// meant it went through the ngrok tunnel during dev testing, which serves an
// interstitial warning page instead of the file for non-interactive
// requests like this — the real reason it never played.
const RINGTONE_SRC = "/sounds/call-voice.mp3";

// ⚠️ TEMP — UI preview only (Rahim, 2026-08-20). Forces the calling UI into a
// mocked state regardless of real Redux call state, so each state's look can
// be checked without needing a real call to reach it live. "ringing" previews
// CallWidget's Answer/Decline pair, "connected" previews its Hang Up state,
// "transfer" previews TransferRequestWidget (the not-yet-redesigned incoming-
// transfer popup — real transfers are rare/hard to trigger on demand).
// A click-through toggle (bottom-right corner) picks which one live in the
// browser. Shapes match what the real socket events/
// useCallConnection actions actually put in state.calls.incoming/.active/
// .incomingTransfer (see callSlice.js + calling.webhook.js's SOCKET_EVENTS
// payloads + callingTransferController.js's transfer-request payload).
// DELETE this whole override once real testing is done — never ship this to
// production.
// Bypassed 2026-08-20 (Rahim) — this widget's z-index (snackbar+1/+2, ~1401)
// sits ABOVE a MUI Dialog's (1300), so it was floating on top of and
// blocking the Create Contact / Filter by Labels dialogs. Flip back to true
// to resume previewing the calling popups.
const USE_MOCK_ACTIVE_CALL = false;
const MOCK_STATES = ["ringing", "connected", "transfer"];
const MOCK_INCOMING_CALL = {
  callId: "mock-incoming-1",
  wacid: "mock-wacid-1",
  contactId: "mock-contact-1",
  contactName: "Ayesha Raza",
  from: "+92 300 1234567",
  bridgeMode: "direct",
};
const MOCK_ACTIVE_CALL = {
  callId: "mock-active-1",
  contactId: "mock-contact-1",
  contactName: "Ayesha Raza",
  remoteNumber: "+92 300 1234567",
  from: "+92 300 1234567",
  direction: "inbound",
  status: "connected",
  connectedAt: Date.now() - 47000,
  // "sfu" (not "direct") so the Transfer button/menu actually shows in the
  // mocked "Connected" state — canTransfer requires bridgeMode:"sfu"
  // (Rahim, 2026-08-20 — was direct, hiding Transfer entirely).
  bridgeMode: "sfu",
};
const MOCK_INCOMING_TRANSFER = {
  callId: "mock-transfer-1",
  contactId: "mock-contact-1",
  contactName: "Ayesha Raza",
  waNumber: "+92 300 1234567",
  fromAgentName: "Zainab Tariq",
};

// Browsers block audio.play() until the page has seen a user gesture at
// least once. A ringtone is inherently unprompted (nobody clicks a button
// right before a call rings), so without this, .play() silently rejects and
// the "no sound" report is just that browser policy, not a bug in the call
// path. Unlock once, on whatever the very first interaction with the page
// happens to be, so by the time a real call rings, playback is already allowed.
let audioUnlocked = false;
function unlockAudioOnFirstInteraction() {
  if (audioUnlocked) return;
  const unlock = () => {
    audioUnlocked = true;
    const el = new Audio(RINGTONE_SRC);
    el.volume = 0;
    el.play().then(() => {
      el.pause();
      el.currentTime = 0;
    }).catch(() => {});
    document.removeEventListener("click", unlock);
    document.removeEventListener("keydown", unlock);
  };
  document.addEventListener("click", unlock, { once: true });
  document.addEventListener("keydown", unlock, { once: true });
}

/**
 * Mount exactly once, near the app root (see App.js) — owns the one
 * RTCPeerConnection this app ever has open, and renders the banner/bar
 * regardless of which page or conversation is currently in view.
 */
export default function CallManager() {
  const dispatch = useDispatch();
  const incoming = useSelector((state) => state.calls.incoming);
  const active = useSelector((state) => state.calls.active);
  const incomingTransfer = useSelector((state) => state.calls.incomingTransfer);
  const error = useSelector((state) => state.calls.error);
  const notificationSettings = useSelector((state) => state.notifications?.settings);
  const { answerIncoming, rejectIncoming, hangup, recoverActiveCall, initiateTransfer, cancelTransfer, respondToTransfer } = useCallConnection();
  useLiveCallsFeed();
  const ringtoneRef = useRef(null);
  const lastRungCallIdRef = useRef(null);
  // ⚠️ TEMP — see MOCK_STATES above. Click-through demo toggle, no code edit
  // needed between states.
  const [mockState, setMockState] = useState(MOCK_STATES[0]);

  useEffect(() => {
    unlockAudioOnFirstInteraction();
  }, []);

  // Runs once per real page load (this component mounts once at the app
  // root, not on route navigation) — recovers a still-ringing call the
  // Redux state wouldn't otherwise know about after a refresh, and cleans
  // up a connected call the reload just silently killed.
  useEffect(() => {
    recoverActiveCall();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(clearCallError());
    }
  }, [error, dispatch]);

  // Ringtone — loops for as long as there's a ring (a new call) or a
  // transfer offer waiting, unanswered. Both cases are structurally
  // exclusive with an active call (see callSlice's incomingTransfer
  // comment), so this one effect covers both without extra branching.
  const ringingEntity = !active ? incoming || incomingTransfer : null;
  useEffect(() => {
    if (ringingEntity) {
      if (!ringtoneRef.current) {
        ringtoneRef.current = new Audio(RINGTONE_SRC);
        ringtoneRef.current.loop = true;
      }
      ringtoneRef.current.play().catch(() => {
        // Autoplay can be blocked before the user has interacted with the page —
        // the visual banner still shows either way, this just loses the sound.
      });

      // OS-level popup too, so a call isn't missed just because the tab isn't focused.
      if (ringingEntity.callId !== lastRungCallIdRef.current) {
        lastRungCallIdRef.current = ringingEntity.callId;
        showBrowserNotification(
          incoming ? "Incoming call" : "Incoming transfer",
          { body: ringingEntity.from || ringingEntity.waNumber, tag: `call-${ringingEntity.callId}`, data: {} },
          notificationSettings || { browserNotificationsEnabled: true }
        );
      }
    } else if (ringtoneRef.current) {
      ringtoneRef.current.pause();
      ringtoneRef.current.currentTime = 0;
    }
  }, [ringingEntity, incoming, notificationSettings]);

  // ⚠️ TEMP — demo-only click-through toggle for MOCK_STATES, so the popup
  // being shown can be switched live in the browser instead of editing code
  // + recompiling between each one. DELETE alongside the rest of the mock
  // override once the demo is done.
  const mockToggle = USE_MOCK_ACTIVE_CALL && (
    <Box
      sx={{
        position: "fixed",
        bottom: 16,
        right: 16,
        zIndex: (theme) => theme.zIndex.snackbar + 2,
        display: "flex",
        gap: 0.5,
        bgcolor: "rgba(0,0,0,0.75)",
        borderRadius: 2,
        p: 0.5,
      }}
    >
      {MOCK_STATES.map((s) => (
        <ButtonBase
          key={s}
          onClick={() => setMockState(s)}
          sx={{
            px: 1.25,
            py: 0.5,
            borderRadius: 1.5,
            fontSize: 12,
            fontWeight: 600,
            color: "#fff",
            textTransform: "capitalize",
            bgcolor: mockState === s ? "#1f7a6c" : "transparent",
          }}
        >
          {s}
        </ButtonBase>
      ))}
    </Box>
  );

  // ⚠️ TEMP — see MOCK_INCOMING_TRANSFER above.
  const displayIncomingTransfer = USE_MOCK_ACTIVE_CALL && mockState === "transfer" ? MOCK_INCOMING_TRANSFER : incomingTransfer;
  if (displayIncomingTransfer && !active) {
    return (
      <>
        <TransferRequestWidget
          transfer={displayIncomingTransfer}
          onAccept={() => respondToTransfer(displayIncomingTransfer, true)}
          onDecline={() => respondToTransfer(displayIncomingTransfer, false)}
        />
        {mockToggle}
      </>
    );
  }

  // ⚠️ TEMP — see MOCK_INCOMING_CALL/MOCK_ACTIVE_CALL above.
  const displayIncoming = USE_MOCK_ACTIVE_CALL && mockState === "ringing" ? MOCK_INCOMING_CALL : incoming;
  const displayActive = USE_MOCK_ACTIVE_CALL && mockState === "connected" ? MOCK_ACTIVE_CALL : active;

  return (
    <>
      {(USE_MOCK_ACTIVE_CALL || incoming || active) && (
        <CallWidget
          incoming={!displayActive ? displayIncoming : null}
          active={displayActive}
          onAnswer={() => answerIncoming(displayIncoming?.callId)}
          onReject={() => rejectIncoming(displayIncoming?.callId)}
          onHangup={() => hangup(displayActive?.callId)}
          onInitiateTransfer={(callId, toAgentId, toAgentName) => initiateTransfer(callId, toAgentId, toAgentName)}
          onCancelTransfer={(callId) => cancelTransfer(callId)}
        />
      )}
      {mockToggle}
    </>
  );
}
